import { Form } from "@inertiajs/react"
import { Send } from "lucide-react"
import { useState } from "react"
import { useTranslation } from "react-i18next"

import {
  AlertDialog,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog"
import { Button } from "@/components/ui/button"
import { publishMasterclassPath } from "@/routes"

/**
 * OKS-54 — Publish-confirm AlertDialog (UX Spec §4 + Show §2.2).
 *
 * Wraps Inertia `<Form>` (Crew rule — no shadcn `Form`, no RHF, no
 * zod). POST to `/brand/masterclasses/:id/publish` (dedicated member
 * route, TRD KD-2). Idempotent server-side.
 *
 * Default focus on the destructive-by-context primary action — this
 * follows the OKS-34 brand-side `MarkComplete` precedent (operator
 * workflow speed > Cancel-by-default). UX Spec §6 for delete keeps
 * Cancel as default focus; publish/unpublish follow the operator-
 * speed pattern.
 */
export interface MasterclassPublishConfirmDialogProps {
  masterclassId: number
}

export function MasterclassPublishConfirmDialog({
  masterclassId,
}: MasterclassPublishConfirmDialogProps) {
  const { t } = useTranslation()
  const [open, setOpen] = useState(false)

  return (
    <AlertDialog open={open} onOpenChange={setOpen}>
      <AlertDialogTrigger
        render={
          <Button data-testid="masterclass-publish-trigger">
            <Send className="mr-1 size-4" aria-hidden="true" />
            {t("masterclasses.show.actions.publish")}
          </Button>
        }
      />
      <AlertDialogContent data-testid="masterclass-publish-dialog">
        <AlertDialogHeader>
          <AlertDialogTitle>
            {t("masterclasses.confirm.publish.title")}
          </AlertDialogTitle>
          <AlertDialogDescription>
            {t("masterclasses.confirm.publish.body")}
          </AlertDialogDescription>
        </AlertDialogHeader>
        <Form
          action={publishMasterclassPath(masterclassId)}
          method="post"
          options={{ preserveScroll: true }}
          onSuccess={() => setOpen(false)}
        >
          {({ processing }) => (
            <AlertDialogFooter>
              <Button
                type="button"
                variant="outline"
                onClick={() => setOpen(false)}
                data-testid="masterclass-publish-cancel"
              >
                {t("masterclasses.confirm.publish.cancel")}
              </Button>
              <Button
                type="submit"
                disabled={processing}
                autoFocus
                data-testid="masterclass-publish-confirm"
              >
                {t("masterclasses.confirm.publish.confirm")}
              </Button>
            </AlertDialogFooter>
          )}
        </Form>
      </AlertDialogContent>
    </AlertDialog>
  )
}
